// Schematic pieces + state-aware placement (STR-2/3). PURE (ENG-1).
//
// A `Schematic` is a compiled structure piece: a palette of block keys plus a
// bottom-up grid of symbols, and typed anchors (chest loot / spawner mob) tagged
// to grid cells. The DSL that authors these lives in `tools/schematic.ts`; both
// the tool and this module share `parseSchematic` so a piece round-trips
// text → JSON → placement identically.
//
// Placement is state-aware: rotating a piece 90° rotates each block's directional
// properties through the property-rotation table (facing/axis/rail/…), and only
// the cells that land in the target chunk are stamped (STR-3 chunk-intersection),
// so a structure spanning several chunks is written seam-free as each chunk
// generates — the stamp of one chunk equals the matching slice of a whole-piece
// stamp (the M6.1 golden).

/** One authored/compiled structure piece. `layers[y][z]` is a row of `size[0]`
 *  symbols (bottom layer first); '.' means "leave the existing block". */
export interface Schematic {
  name: string;
  /** [width(x), height(y), depth(z)] in blocks. */
  size: readonly [number, number, number];
  /** Symbol → block key (`oak_stairs[facing=north,half=bottom]`). '.' is reserved. */
  palette: Readonly<Record<string, string>>;
  /** Symbol → typed anchor placed at that cell (block entity seed). */
  anchors: Readonly<Record<string, SchematicAnchor>>;
  /** `layers[y]` = `size[2]` rows of `size[0]` chars, bottom-up. */
  layers: readonly (readonly string[])[];
}

export interface SchematicAnchor {
  type: 'chest' | 'spawner' | 'jigsaw';
  /** Chest loot-table name, spawner mob id, or jigsaw connection name. */
  data: string;
}

/** '.' = keep the existing block (structures don't carve air unless told to). */
export const KEEP_SYMBOL = '.';
/** '_' = carve air, so a buried piece hollows out its own rooms. Seeded into every
 *  palette by `parseSchematic`; an explicit `_ = <block>` line still overrides it. */
export const AIR_SYMBOL = '_';
/** A stamped anchor at a resolved world position. */
export interface PlacedAnchor {
  x: number;
  y: number;
  z: number;
  type: SchematicAnchor['type'];
  data: string;
}

// ── Property rotation table (STR-3) ──────────────────────────────────────────
// Rotate CW by 90° viewed from above, matching Minecraft facing N→E→S→W (north
// = −Z, east = +X, south = +Z, west = −X). Only directional properties change;
// `shape`/`hinge` are relative to `facing` so they ride along untouched, and
// vertical/boolean properties (half, open, powered, waterlogged…) are invariant
// under a horizontal turn.
const FACING_CW = ['north', 'east', 'south', 'west'] as const;
const RAIL_ASCEND_CW = ['ascending_north', 'ascending_east', 'ascending_south', 'ascending_west'] as const;
const RAIL_CURVE_CW = ['north_east', 'south_east', 'south_west', 'north_west'] as const;

function rotateInRing(ring: readonly string[], value: string, k: number): string | null {
  const i = ring.indexOf(value);
  if (i < 0) return null;
  return ring[(i + k) % ring.length]!;
}

/** Rotate a single `propName=value` by `k` quarter-turns CW. Unknown/invariant
 *  properties return their value unchanged. */
export function rotateProperty(prop: string, value: string, k: number): string {
  const q = ((k % 4) + 4) % 4;
  if (q === 0) return value;
  switch (prop) {
    case 'facing':
      return rotateInRing(FACING_CW, value, q) ?? value;
    case 'axis':
      // x↔z on an odd turn; y (vertical) is fixed.
      if (q % 2 === 0) return value;
      return value === 'x' ? 'z' : value === 'z' ? 'x' : value;
    case 'shape': {
      // Rail direction shapes rotate; stair corner shapes are facing-relative.
      if (value === 'north_south' || value === 'east_west') {
        return q % 2 === 0 ? value : value === 'north_south' ? 'east_west' : 'north_south';
      }
      return rotateInRing(RAIL_ASCEND_CW, value, q) ?? rotateInRing(RAIL_CURVE_CW, value, q) ?? value;
    }
    default:
      return value;
  }
}

/** Rotate a whole property map by `k` quarter-turns CW. */
export function rotateProps(props: Readonly<Record<string, string>>, k: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [p, v] of Object.entries(props)) out[p] = rotateProperty(p, v, k);
  return out;
}

/** Rotate a horizontal offset (dx,dz) by `k` quarter-turns CW (from above). */
export function rotateOffset(dx: number, dz: number, k: number): [number, number] {
  let x = dx, z = dz;
  for (let i = 0; i < (((k % 4) + 4) % 4); i++) { const nx = -z; z = x; x = nx; }
  return [x, z];
}

// ── Block-key parsing ────────────────────────────────────────────────────────
export interface ParsedKey {
  name: string;
  props: Record<string, string>;
}

/** Parse `name` or `name[k=v,k2=v2]` into a base name + property map. */
export function parseBlockKey(key: string): ParsedKey {
  const br = key.indexOf('[');
  if (br < 0) return { name: key, props: {} };
  const name = key.slice(0, br);
  const inner = key.slice(br + 1, key.lastIndexOf(']'));
  const props: Record<string, string> = {};
  for (const pair of inner.split(',')) {
    const eq = pair.indexOf('=');
    if (eq > 0) props[pair.slice(0, eq).trim()] = pair.slice(eq + 1).trim();
  }
  return { name, props };
}

/**
 * Resolve a (possibly-rotated) block key to a state id against the registry's
 * StateTable, matching by base name + property map (order-independent) so an
 * authored key need not list properties in declaration order. Falls back to the
 * block's default state, then air, when a property combination doesn't exist.
 */
export interface StateResolver {
  statesOf(name: string): readonly number[] | undefined;
  propsOf(stateId: number): Readonly<Record<string, string>>;
  defaultOf(name: string): number | undefined;
}

export function resolveRotatedState(res: StateResolver, key: string, k: number): number {
  const { name, props } = parseBlockKey(key);
  const rotated = rotateProps(props, k);
  const states = res.statesOf(name);
  if (!states) return res.defaultOf(name) ?? 0;
  if (Object.keys(rotated).length === 0) return states[0]!;
  for (const s of states) {
    const sp = res.propsOf(s);
    let match = true;
    for (const [p, v] of Object.entries(rotated)) {
      if (sp[p] !== v) { match = false; break; }
    }
    if (match) return s;
  }
  return res.defaultOf(name) ?? states[0]!;
}

// ── Stamping (STR-3) ─────────────────────────────────────────────────────────
export interface StampTarget {
  /** Set the block at world (x,y,z); the caller filters to a chunk / bounds. */
  set(x: number, y: number, z: number, state: number): void;
  /** Record a block-entity anchor at world (x,y,z). */
  anchor?(a: PlacedAnchor): void;
}

/**
 * Stamp `schem` with its local (0,0,0) at world `origin`, rotated `k`×90° CW.
 * Cells whose symbol is '.' or maps to nothing are skipped; every other cell is
 * resolved to a state (rotated) and written via `target.set`, which is free to
 * ignore out-of-chunk positions — that is what makes chunk-intersection stamping
 * agree with a whole-piece stamp.
 */
export function stampSchematic(
  schem: Schematic,
  origin: readonly [number, number, number],
  k: number,
  res: StateResolver,
  target: StampTarget,
): void {
  const [w, h, d] = schem.size;
  const [ox, oy, oz] = origin;
  const stateCache = new Map<string, number>();
  const resolve = (key: string): number => {
    const ck = `${key}#${k}`;
    let s = stateCache.get(ck);
    if (s === undefined) { s = resolveRotatedState(res, key, k); stateCache.set(ck, s); }
    return s;
  };
  for (let ly = 0; ly < h; ly++) {
    const layer = schem.layers[ly];
    if (!layer) continue;
    for (let lz = 0; lz < d; lz++) {
      const row = layer[lz] ?? '';
      for (let lx = 0; lx < w; lx++) {
        const sym = row[lx] ?? KEEP_SYMBOL;
        if (sym === KEEP_SYMBOL || sym === ' ') continue;
        const key = schem.palette[sym];
        if (key === undefined) continue;
        const [rx, rz] = rotateOffset(lx, lz, k);
        const wx = ox + rx, wy = oy + ly, wz = oz + rz;
        target.set(wx, wy, wz, resolve(key));
        const anch = schem.anchors[sym];
        if (anch && target.anchor) target.anchor({ x: wx, y: wy, z: wz, type: anch.type, data: anch.data });
      }
    }
  }
}

/** The world-space AABB (min/max block coords) a piece occupies at `origin`/`k`. */
export function schematicBounds(
  schem: Schematic,
  origin: readonly [number, number, number],
  k: number,
): { minX: number; minZ: number; maxX: number; maxZ: number; minY: number; maxY: number } {
  const [w, h, d] = schem.size;
  const [ox, oy, oz] = origin;
  // Rotation keeps the box axis-aligned; take the four rotated corners.
  const corners: [number, number][] = [
    rotateOffset(0, 0, k), rotateOffset(w - 1, 0, k),
    rotateOffset(0, d - 1, k), rotateOffset(w - 1, d - 1, k),
  ];
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (const [cx, cz] of corners) {
    minX = Math.min(minX, cx); maxX = Math.max(maxX, cx);
    minZ = Math.min(minZ, cz); maxZ = Math.max(maxZ, cz);
  }
  return { minX: ox + minX, maxX: ox + maxX, minZ: oz + minZ, maxZ: oz + maxZ, minY: oy, maxY: oy + h - 1 };
}

// ── DSL parser (shared by the compiler tool and tests) ───────────────────────
// Format (see tools/schematic.ts for the full grammar):
//   name village/house_small
//   size 5 4 5                     # optional; inferred from layers otherwise
//   palette
//   # = cobblestone
//   P = oak_planks
//   C = chest @chest:chests/village_house
//   S = spawner @spawner:zombie
//   layer                          # one per Y, bottom-up
//   #####
//   #...#
//   ...
export function parseSchematic(text: string): Schematic {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let name = 'unnamed';
  let declaredSize: [number, number, number] | null = null;
  const palette: Record<string, string> = { [KEEP_SYMBOL]: KEEP_SYMBOL, [AIR_SYMBOL]: 'air' };
  const anchors: Record<string, SchematicAnchor> = {};
  const layers: string[][] = [];
  let mode: 'none' | 'palette' | 'layer' = 'none';
  let current: string[] | null = null;

  const flushLayer = (): void => { if (current) { layers.push(current); current = null; } };

  for (const raw of lines) {
    const line = raw.replace(/\t/g, '  ');
    const trimmed = line.trim();
    if (mode === 'layer') {
      // A blank line or a new directive ends the current layer.
      if (trimmed === '' || /^(name|size|palette|layer)\b/.test(trimmed)) {
        flushLayer();
        mode = 'none';
      } else {
        (current ??= []).push(line.replace(/\s+$/, ''));
        continue;
      }
    }
    if (trimmed === '') continue;
    const [head, ...rest] = trimmed.split(/\s+/);
    if (head === 'name') { name = rest.join(' '); mode = 'none'; continue; }
    if (head === 'size') {
      const [a, b, c] = rest.map(Number);
      declaredSize = [a ?? 0, b ?? 0, c ?? 0];
      mode = 'none';
      continue;
    }
    if (head === 'palette') { mode = 'palette'; continue; }
    if (head === 'layer') { flushLayer(); current = []; mode = 'layer'; continue; }
    if (mode === 'palette') {
      // `X = block_key [@anchorType:data]`
      const m = /^(\S)\s*=\s*(\S+)(?:\s+@(\w+):(\S+))?/.exec(trimmed);
      if (!m) continue;
      const [, sym, block, anchorType, anchorData] = m;
      palette[sym!] = block!;
      if (anchorType && anchorData) {
        anchors[sym!] = { type: anchorType as SchematicAnchor['type'], data: anchorData };
      }
    }
  }
  flushLayer();

  const height = layers.length;
  const depth = Math.max(0, ...layers.map((l) => l.length));
  const width = Math.max(0, ...layers.flatMap((l) => l.map((r) => r.length)));
  // Normalize every row to full width and every layer to full depth (pad '.').
  const norm = layers.map((layer) => {
    const rows = layer.slice();
    while (rows.length < depth) rows.push('');
    return rows.map((r) => r.padEnd(width, KEEP_SYMBOL));
  });
  const size: [number, number, number] = declaredSize ?? [width, height, depth];
  return { name, size, palette, anchors, layers: norm };
}
